import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { sitesApi, blogApi, pagesApi } from '../../api/client';
import {
  combinedCount,
  excessCombinedForSite,
  isAllContentSelectionValid,
  isValidSiteDeletionSelection,
  minSitesUserMustDelete,
  needsContentDeletionStep,
  needsSiteDeletionStep,
  snapshotsForRemainingSites,
} from './downgradeCompliance';
import { formatPagesPerSiteLabel, isUnlimitedPages } from './planChange';
import type { DowngradeResourceWizardPayload } from './usePricingState';

type Step = 1 | 2;

function toggleInSet<T>(set: Set<T>, value: T, checked: boolean): Set<T> {
  const n = new Set(set);
  if (checked) n.add(value);
  else n.delete(value);
  return n;
}

export interface DowngradeResourceWizardProps {
  isOpen: boolean;
  config: DowngradeResourceWizardPayload;
  onClose: () => void;
  onResourceCleanupDone: () => void;
}

export const DowngradeResourceWizard: React.FC<DowngradeResourceWizardProps> = ({
  isOpen,
  config,
  onClose,
  onResourceCleanupDone,
}) => {
  const { maxSites, maxPagesPerSite, snapshots, planDisplayName } = config;
  const unlimitedPps = isUnlimitedPages(maxPagesPerSite);
  const needStep1 = needsSiteDeletionStep(snapshots.length, maxSites);

  const [step, setStep] = useState<Step>(1);
  const [siteIdsToDelete, setSiteIdsToDelete] = useState<Set<string>>(() => new Set());
  const [pageDeleteBySite, setPageDeleteBySite] = useState<Record<string, Set<string>>>({});
  const [postDeleteBySite, setPostDeleteBySite] = useState<Record<string, Set<string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep(needStep1 ? 1 : 2);
    setSiteIdsToDelete(new Set());
    setPageDeleteBySite({});
    setPostDeleteBySite({});
    setError(null);
  }, [isOpen, needStep1, config.snapshots]);

  const remainingAfterSites = useMemo(
    () => snapshotsForRemainingSites(snapshots, siteIdsToDelete),
    [snapshots, siteIdsToDelete]
  );

  const getPageDels = useCallback(
    (siteId: string) => pageDeleteBySite[siteId] ?? new Set<string>(),
    [pageDeleteBySite]
  );
  const getPostDels = useCallback(
    (siteId: string) => postDeleteBySite[siteId] ?? new Set<string>(),
    [postDeleteBySite]
  );

  const hasContentWork = useMemo(
    () => !unlimitedPps && needsContentDeletionStep(remainingAfterSites, maxPagesPerSite),
    [remainingAfterSites, maxPagesPerSite, unlimitedPps]
  );

  const siteStepValid = isValidSiteDeletionSelection(
    snapshots.length,
    maxSites,
    siteIdsToDelete.size
  );

  const contentValid = isAllContentSelectionValid(
    remainingAfterSites,
    maxPagesPerSite,
    getPageDels,
    getPostDels
  );
  const canGoNextFromStep1 = siteStepValid;
  // contentValid is used in step2 button

  const runDeletions = async () => {
    setSubmitting(true);
    setError(null);
    try {
      for (const id of siteIdsToDelete) {
        await sitesApi.delete(id);
      }
      for (const site of remainingAfterSites) {
        const pd = getPageDels(site.siteId);
        const pbd = getPostDels(site.siteId);
        for (const pageId of pd) {
          await pagesApi.delete(pageId);
        }
        for (const postId of pbd) {
          await blogApi.posts.delete(site.siteId, postId);
        }
      }
      onResourceCleanupDone();
    } catch (e: unknown) {
      const msg =
        e instanceof Error ? e.message : 'Something went wrong while deleting. Try again.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const ppsLabel = unlimitedPps
    ? 'unlimited pages and blog posts per site (combined)'
    : `up to ${formatPagesPerSiteLabel(maxPagesPerSite)} pages and blog posts per site (combined)`;

  const onStep1Next = () => {
    if (hasContentWork) setStep(2);
    else void runDeletions();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          Free up space for {planDisplayName}
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          The {planDisplayName} plan allows {maxSites === 1 ? '1 site' : `up to ${maxSites} sites`}{' '}
          and {ppsLabel}. Remove enough sites, pages, and blog posts to meet the new limits before
          the plan change.
        </p>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}

        {step === 1 && needStep1 && (
          <div className="mt-6">
            <h3 className="font-medium text-gray-900 mb-2">Step 1: Sites to remove</h3>
            <p className="text-sm text-gray-600 mb-3">
              Select at least {minSitesUserMustDelete(snapshots.length, maxSites)} site
              {minSitesUserMustDelete(snapshots.length, maxSites) === 1 ? '' : 's'} to delete.
            </p>
            <ul className="space-y-2 max-h-64 overflow-y-auto border rounded p-2">
              {snapshots.map(s => (
                <li key={s.siteId} className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={siteIdsToDelete.has(s.siteId)}
                    onChange={e => {
                      setSiteIdsToDelete(prev => toggleInSet(prev, s.siteId, e.target.checked));
                    }}
                    id={`del-site-${s.siteId}`}
                  />
                  <label htmlFor={`del-site-${s.siteId}`} className="text-sm text-gray-800">
                    <span className="font-medium">{s.siteName}</span>
                    <span className="text-gray-500">
                      {' '}
                      — {s.pageCount} page{s.pageCount === 1 ? '' : 's'}
                      {s.blogPostCount > 0
                        ? `, ${s.blogPostCount} blog post${s.blogPostCount === 1 ? '' : 's'}`
                        : ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 mt-2">
              Deleting a site removes all its pages and blog content for that site.
            </p>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onStep1Next}
                disabled={!canGoNextFromStep1 || submitting}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {hasContentWork ? 'Next' : 'Delete and continue'}
              </button>
            </div>
          </div>
        )}

        {step === 2 && hasContentWork && !unlimitedPps && (
          <div className="mt-6">
            <h3 className="font-medium text-gray-900 mb-2">
              {needStep1 ? 'Step 2: ' : ''}Pages and blog posts to remove
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              For each site you are keeping, select items to delete so the remaining total (pages +
              blog posts) is at most {formatPagesPerSiteLabel(maxPagesPerSite)}.
            </p>
            <div className="space-y-6 max-h-80 overflow-y-auto pr-1">
              {remainingAfterSites.map(site => {
                if (excessCombinedForSite(site, maxPagesPerSite) === 0) return null;
                return (
                  <div key={site.siteId} className="border rounded p-3">
                    <p className="text-sm font-medium text-gray-800 mb-1">{site.siteName}</p>
                    <p className="text-xs text-amber-800 mb-2">
                      Currently {combinedCount(site)} combined (limit {maxPagesPerSite}).
                    </p>
                    <ul className="space-y-1.5 pl-0 list-none">
                      {site.pages.map(p => (
                        <li key={p.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`p-${p.id}`}
                            checked={getPageDels(site.siteId).has(p.id)}
                            onChange={e => {
                              setPageDeleteBySite(prev => {
                                const cur = prev[site.siteId] ?? new Set<string>();
                                return {
                                  ...prev,
                                  [site.siteId]: toggleInSet(cur, p.id, e.target.checked),
                                };
                              });
                            }}
                          />
                          <label htmlFor={`p-${p.id}`} className="text-sm text-gray-700">
                            Page: {p.title}
                          </label>
                        </li>
                      ))}
                      {site.posts.map(p => (
                        <li key={p.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`b-${p.id}`}
                            checked={getPostDels(site.siteId).has(p.id)}
                            onChange={e => {
                              setPostDeleteBySite(prev => {
                                const cur = prev[site.siteId] ?? new Set<string>();
                                return {
                                  ...prev,
                                  [site.siteId]: toggleInSet(cur, p.id, e.target.checked),
                                };
                              });
                            }}
                          />
                          <label htmlFor={`b-${p.id}`} className="text-sm text-gray-700">
                            Blog: {p.title}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
              >
                Cancel
              </button>
              {needStep1 && (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  disabled={submitting}
                  className="px-4 py-2 rounded-md border border-gray-300 text-gray-700"
                >
                  Back
                </button>
              )}
              <button
                type="button"
                onClick={() => void runDeletions()}
                disabled={!contentValid || submitting}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Delete and continue
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
